import React, { useState } from "react";
import { useSite } from "../context/site";
import { track } from "../lib/analytics";
import { CONTACT_EMAIL } from "../lib/content";
import { PageHero } from "../components/site/kit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "font-body w-full border border-[#161A15]/25 bg-white px-4 py-3.5 text-lg font-normal text-black outline-none transition-colors placeholder:text-black/45 focus:border-[#5f8f10]";

export default function Contact() {
  const { openDiagnostic } = useSite();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    track("contact_message_submitted");
    try {
      const res = await fetch(`${API}/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error("bad status");
      setSent(true);
    } catch (err) {
      setError(`We couldn't send your message right now. Please try again, or write to ${CONTACT_EMAIL}.`);
      track("contact_message_error");
    }
  };

  return (
    <div className="font-body bg-white">
      <PageHero
        testId="contact-hero"
        eyebrow="Contact"
        crumbs={[{ label: "Home", href: "/" }, { label: "Contact" }]}
        title={<>Let&rsquo;s <span className="text-[#5f8f10]">talk.</span></>}
        subcopy="For a business growth challenge, start with the diagnostic. For everything else, send us a message."
      />

      <section className="border-b border-black/10 bg-[#F7F6F2]">
        <div className="mx-auto grid max-w-[1360px] gap-0 border border-black/15 md:grid-cols-2 md:my-0">
          <div className="flex flex-col justify-between border-b border-white/12 bg-[#161A15] p-8 text-white md:border-b-0 md:border-r md:border-white/12 md:p-12">
            <div>
              <h2 className="font-display text-3xl uppercase leading-[0.95] tracking-tight md:text-4xl">Bring us your growth problem</h2>
              <p className="font-body mt-6 max-w-md text-lg font-normal leading-relaxed text-white/85">
                Start with what is not moving, what has changed, or what the business is trying to achieve. A short, guided diagnostic, not a long form.
              </p>
            </div>
            <button type="button" data-testid="contact-diagnostic-cta" onClick={() => { track("contact_diagnostic_cta_clicked"); openDiagnostic(); }} className="font-body mt-10 inline-flex w-fit items-center gap-3 border-2 border-[#5f8f10] bg-[#5f8f10] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#161A15] transition-colors duration-150 hover:border-white hover:bg-white">
              Bring Us Your Growth Problem <span aria-hidden>→</span>
            </button>
          </div>

          <div className="bg-white p-8 md:p-12">
            <h2 className="font-display text-3xl uppercase leading-[0.95] tracking-tight text-black md:text-4xl">Send us a message</h2>
            {sent ? (
              <div data-testid="contact-confirm" className="mt-8 border border-black/15 p-6">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center bg-[#5f8f10] text-xl font-bold text-[#161A15]">✓</div>
                <p className="font-body text-lg font-normal leading-relaxed text-black/85">Thank you, your message has been received. We&rsquo;ll be in touch.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-4">
                <input required data-testid="contact-name" className={field} value={form.name} onChange={set("name")} placeholder="Name" />
                <input required type="email" data-testid="contact-email" className={field} value={form.email} onChange={set("email")} placeholder="Work email" />
                <input data-testid="contact-company" className={field} value={form.company} onChange={set("company")} placeholder="Company" />
                <textarea required rows={4} data-testid="contact-message" className={`${field} resize-none`} value={form.message} onChange={set("message")} placeholder="Message" />
                {error && <p data-testid="contact-error" className="font-body text-base font-semibold text-[#b23b2e]">{error}</p>}
                <button type="submit" data-testid="contact-submit" className="font-body inline-flex w-full items-center justify-center gap-3 border-2 border-[#161A15] bg-[#161A15] px-6 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-150 hover:bg-[#5f8f10] hover:border-[#5f8f10] hover:text-[#161A15]">
                  Send Message <span aria-hidden>→</span>
                </button>
              </form>
            )}
            <p className="font-body mt-6 text-base font-normal text-black/70">
              Or write to{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} onClick={() => track("contact_email_clicked")} className="font-semibold text-black underline underline-offset-4">{CONTACT_EMAIL}</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
